import React, { useState, useEffect, useMemo } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Statuses } from "../../../../../../../../models/statuses";
import { Status, SubStatusUsedBy } from "../../../../../../../../util/enums";
import Input from "../../../../../../../../components/Form/Input";
import Select from "../../../../../../../../components/Form/Select";
import Modal from "../../../../../../../../components/Generic/Modal";
import useStyles from "./styles";

interface Props {
  status?: Statuses;
  open: boolean;
  hasError?: boolean;
  isLoading?: boolean;
  closeModal: () => void;
  updateOrCreateStatus: (status: Statuses) => void;
}

const JobSubStatusModal = ({
  status,
  open,
  hasError,
  isLoading = false,
  closeModal,
  updateOrCreateStatus,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [name, setName] = useState<string>("");
  const [usedBy, setUsedBy] = useState<string>("");
  const [statusField, setStatusField] = useState<string>(Status.INACTIVE);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const usedByOptions = useMemo(
    () => [
      { value: SubStatusUsedBy.ESTIMATES, label: SubStatusUsedBy.ESTIMATES },
      { value: SubStatusUsedBy.ORDERS, label: SubStatusUsedBy.ORDERS },
    ],
    []
  );

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (type) =>
        type ? type !== "" : false
      ),
    usedBy: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const clearFields = () => {
    setName("");
    setUsedBy("");
    setStatusField(Status.INACTIVE);
  };

  const isValid = (status: Statuses) => {
    try {
      schema.validateSync(
        {
          name: status.name,
          usedBy: status.usedBy,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    const object = {
      name,
      usedBy,
      status: statusField,
    } as Statuses;

    if (isValid(object)) updateOrCreateStatus(object);
  };

  useEffect(() => {
    if (status) {
      setName(status?.name ? status?.name : "");
      setUsedBy(status?.usedBy ? status?.usedBy : "");
      setStatusField(status?.status ? status?.status : "");
    }
  }, [status]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      const object = {
        name,
        usedBy,
      } as Statuses;

      isValid(object);
    }
  }, [name, usedBy, hasFormBeenSubmitted]);

  return (
    <Modal
      data-cy="jobSubStatusModal"
      open={open}
      handleClose={closeModal}
      title={status ? translate("editStatus") : translate("createNewStatus")}
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        loading: isLoading,
        saveDisabled: isLoading,
        textSave: status ? translate("update") : translate("create"),
        dataCySave: "submitButton",
        onSave: handleSubmit,
      }}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        label: translate("status"),
        value: statusField,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatusField(value),
      }}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isLoading}
              name="name"
              label={translate("name")}
              placeholder={translate("name")}
              error={!!getErrorByField("name")}
              errorMessage={getErrorByField("name")}
              value={name}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Select
              data-cy="usedBySelect"
              name="usedBy"
              label={translate("usedBy")}
              value={usedBy}
              disabled={isLoading}
              error={!!getErrorByField("usedBy")}
              errorMessage={getErrorByField("usedBy")}
              onChange={(event: SelectChangeEvent<any>) => setUsedBy(event.target.value)}
              placeholder={translate("selectAnOption")}
            >
              {usedByOptions?.map((option) => (
                <MenuItem data-cy={option.value} value={option.value} key={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default JobSubStatusModal;
